import {
  Row,
  Col,
  Collapse,
  InputNumber,
  Form,
  Select,
  Table,
  Tabs,
  Image,
  Upload,
  message,
} from "antd";
import styles from "./task-details.module.css";
import {
  CheckCircleFilled,
  CheckCircleTwoTone,
  CloseOutlined,
  EyeOutlined,
  UserOutlined,
  UploadOutlined,
  PaperClipOutlined,
  ArrowRightOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import useFetch from "../../hooks/useFetch";
import useToast from "../../hooks/useToast";
import {
  ePriority,
  ePrivileges,
  eResultCode,
  eDocType,
} from "../../utils/enum";
import { ToastOpen, ToastType } from "../../state/toast/slice";
import {
  AddEditTask,
  AddTaskComment,
  GetSpecificTask,
  GetUserList,
} from "../../utils/api.constant";
import Loader from "../loader/loader";
import React, { useState, useRef, useEffect, useContext } from "react";
import JoditEditor from "jodit-react";
import { Button } from "antd";
import FormItemLabel from "antd/es/form/FormItemLabel";
import { PageSize } from "../../utils/constants";
import { TDropdownOptions, TUser } from "../../types/config";
import TimelineComponent from "./task-history";
import Utils from "../../utils";
import { UserContext } from "../../context";

interface TaskDetailsModalProps {
  id: number;
  setDroppedTaskId?: (id: number) => void;
  statusOptions?: TDropdownOptions[];
  setIsModalOpen?: (isOpen: boolean) => void;
  currentTargetCategory?: any;
  currentDragEvent?: React.DragEvent<HTMLDivElement>;
  handleDrop?: any;
  currentDropData?: any;
  fetchSpecificProjectDashboardData?: (id: string) => void;
  selectedProject?: string;
  handleTaskUpdate?: (task: any) => void;
  isNotificationView?: boolean; // Read-only mode for non-assigned users
  setTargetStatusId?: (statusId: number) => void;
}

type TComment = {
  id: number;
  user: string;
  timestamp: string;
  content: string;
};
const dummyComments: TComment[] = [
  {
    id: 1,
    user: "John Doe",
    timestamp: "2024-03-15T14:30:00",
    content: "Initial review completed. Waiting for client feedback.",
  },
  {
    id: 2,
    user: "Alice Smith",
    timestamp: "2024-03-16T09:15:00",
    content: "Client requested minor changes. Updated the design files.",
  },
];

const { Panel } = Collapse;

const TaskDetailsModal: React.FC<TaskDetailsModalProps> = ({
  id,
  setDroppedTaskId,
  setIsModalOpen,
  statusOptions,
  currentTargetCategory,
  currentDragEvent,
  handleDrop,
  currentDropData,
  fetchSpecificProjectDashboardData,
  selectedProject,
  handleTaskUpdate,
  isNotificationView = false,
  setTargetStatusId,
}) => {
  const { post } = useFetch();
  const [taskDetails, setTaskDetails] = useState<any>(null);
  const [historyDetails, setHistoryDetails] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { onShowToast } = useToast();
  const [comments, setComments] = useState<TComment[]>([]);
  const [activeCommentKeys, setActiveCommentKeys] = useState<string[]>([]);
  const [activeFileKeys, setActiveFileKeys] = useState<string[]>([]);
  const [actualTime, setActualTime] = useState<number | null>(null);
  const [form] = Form.useForm();
  const [memberIds, setMemberIds] = useState<any>([]);
  const [memberOptions, setMemberOptions] = useState<any>([]);
  const [fileList, setFileList] = useState<any[]>([]);
  const [uploading, setUploading] = useState(false);
  const context = useContext(UserContext);

  // Dummy comments data

  const { TabPane } = Tabs;

  // Helper function to check if file is an image
  const isImageFile = (fileName: string) => {
    const imageExtensions = [
      ".jpg",
      ".jpeg",
      ".png",
      ".gif",
      ".bmp",
      ".webp",
      ".svg",
    ];
    return imageExtensions.some((ext) => fileName.toLowerCase().endsWith(ext));
  };

  const fetchSpecifTask = async (id: number) => {
    try {
      const response = await post(GetSpecificTask, {
        data: {
          id: id,
        },
      });
      const { data, dataResponse } = response;

      if (dataResponse.returnCode === eResultCode.SUCCESS) {
        setTaskDetails(data.task);
        setHistoryDetails(data);
        setActualTime(data.task.actualTime);
        if (data.task.taskComments) {
          setComments(
            data.task.taskComments?.map(
              (comment: {
                id: number;
                userDetails: { userName: string };
                createdon: string;
                comment: string;
              }) => ({
                id: comment.id,
                user: comment.userDetails.userName || "Unknown",
                timestamp: comment.createdon,
                content: comment.comment,
              }),
            ),
          );
        }
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseOutlined />,
          position: ToastOpen.centerTop,
          content: dataResponse.description,
        });
      }
    } catch (error) {
      console.error("Error fetching project options:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchMembers = async () => {
    try {
      const response = await post(GetUserList, {
        data: { pageSize: PageSize, currentPage: 1 },
      });
      const { data, dataResponse } = response;

      if (dataResponse.returnCode === eResultCode.SUCCESS) {
        setMemberOptions(
          data.map((item: TUser) => ({
            id: item.id,
            name: item.fullName,
          })),
        );
      } else {
        console.error("Error fetching clients:", dataResponse.description);
      }
    } catch (error) {
      console.error("Error fetching clients:", error);
    }
  };

  const [comment, setComment] = useState("");
  const editor = useRef(null);

  const config = {
    readonly: false,
    height: 100,
    toolbarAdaptive: false,
    toolbarSticky: true,
    buttons: "bold,italic,underline,ul,ol,font,fontsize,align,link",
    style: {
      fontFamily: "inherit",
      fontSize: "14px",
    },
  };

  const updateTask = async (task: any) => {
    try {
      const updatedTask = {
        ...task,
        statusId: currentDropData
          ? currentDropData.targetCategory.statusId
          : task.statusId, // Update status to target
        actualTime: actualTime,
      };

      // Use the handleTaskUpdate prop if available (for drag & drop scenarios)
      if (handleTaskUpdate) {
        handleTaskUpdate(updatedTask);
        return;
      }

      // Fallback to direct API call
      const response = await post(AddEditTask, {
        data: updatedTask,
      });

      if (response.dataResponse.returnCode === eResultCode.SUCCESS) {
        onShowToast({
          type: ToastType.success,
          title: <CheckCircleFilled />,
          position: ToastOpen.centerTop,
          content: "Task updated successfully",
        });

        // Refresh project data to reflect changes
        if (selectedProject) {
          fetchSpecificProjectDashboardData &&
            fetchSpecificProjectDashboardData(selectedProject);
        }

        // Close modal and reset
        if (setIsModalOpen) {
          setIsModalOpen(false);
        }
        if (setDroppedTaskId) {
          setDroppedTaskId(0);
        }
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseOutlined />,
          position: ToastOpen.centerTop,
          content: "Failed to update task",
        });
      }
    } catch (error) {
      console.error("Error updating task:", error);
      onShowToast({
        type: ToastType.error,
        title: <CloseOutlined />,
        position: ToastOpen.centerTop,
        content: "Error updating task",
      });
    }
  };

  useEffect(() => {
    if (id) {
      fetchSpecifTask(id);
      fetchMembers();
    }
  }, []);

  const updateTaskComments = async (taskId: number, comment: string) => {
    try {
      const response = await post(AddTaskComment, {
        data: {
          taskId: taskId,
          comment: comment,
          notifyto: memberIds,
        },
      });
      const { dataResponse } = response;

      if (dataResponse.returnCode === eResultCode.SUCCESS) {
        setMemberIds([]);
        setComment("");
        onShowToast({
          type: ToastType.success,
          title: <CheckCircleFilled />,
          position: ToastOpen.centerTop,
          content: "Comment added successfully",
        });
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseOutlined />,
          position: ToastOpen.centerTop,
          content: dataResponse.description,
        });
      }
    } catch (error) {
      console.error("Error updating task comments:", error);
    }
  };

  const postComment = async (taskId: number) => {
    // Extract plain text from HTML content first
    const plainTextComment =
      new DOMParser().parseFromString(comment, "text/html").body.textContent ||
      "";

    // Validate the plain text content
    if (plainTextComment.trim() === "") {
      onShowToast({
        type: ToastType.error,
        title: <CloseOutlined />,
        position: ToastOpen.centerTop,
        content: "Comment cannot be empty",
      });
      return;
    }

    setComments([
      ...comments,
      {
        id: comments.length + 1,
        user: "You",
        timestamp: new Date().toISOString(),
        content: plainTextComment,
      },
    ]);
    setComment("");
    setActiveCommentKeys([]);
    updateTaskComments(taskId, plainTextComment);
  };

  const uploadAttachments = async () => {
    if (fileList.length === 0) {
      message.warning("Please select files to upload");
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();

      fileList.forEach((file) => {
        formData.append("files", file.originFileObj || file);
      });

      formData.append("taskId", taskDetails?.id?.toString() || "");
      formData.append("projectId", selectedProject || "");
      formData.append("docType", eDocType.TASK_MEDIA.toString());

      const uploadResponse = await post("/uploadMedia", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (uploadResponse.dataResponse.returnCode === eResultCode.SUCCESS) {
        onShowToast({
          type: ToastType.success,
          title: <CheckCircleFilled />,
          position: ToastOpen.centerTop,
          content: "Files uploaded successfully",
        });

        // Clear the file list
        setFileList([]);

        // Refresh task details to show new attachments
        if (taskDetails?.id) {
          fetchSpecifTask(taskDetails.id);
        }
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseOutlined />,
          position: ToastOpen.centerTop,
          content: uploadResponse.dataResponse.description || "Upload failed",
        });
      }
    } catch (error) {
      console.error("Error uploading files:", error);
      onShowToast({
        type: ToastType.error,
        title: <CloseOutlined />,
        position: ToastOpen.centerTop,
        content: "Error uploading files",
      });
    } finally {
      setUploading(false);
    }
  };

  const handleFileChange = ({ fileList: newFileList }: any) => {
    setFileList(newFileList);
  };

  const beforeUpload = (file: any) => {
    const isLt10M = file.size / 1024 / 1024 < 10;
    if (!isLt10M) {
      message.error("File must be smaller than 10MB!");
      return false;
    }
    return false; // Prevent automatic upload
  };

  return (
    <>
      {!isLoading ? (
        <div>
          <Tabs defaultActiveKey="1" className={styles.tabs}>
            <TabPane
              tab="Task Details"
              key="1"
              className={styles.modalContainer}
            >
              <div className={styles.modalContent}>
                {/* Task Metadata Section */}
                <div className={styles.taskMeta}>
                  <div className={styles.metaGrid}>
                    <div className={styles.metaItem}>
                      <span className={styles.metaLabel}>Priority</span>
                      <span
                        className={`${styles.priority} ${styles[`priority-${taskDetails?.priority}`]}`}
                      >
                        {taskDetails?.priority
                          ? ePriority[taskDetails?.priority]
                          : "N/A"}
                      </span>
                    </div>
                    <div className={styles.metaItem}>
                      <span className={styles.metaLabel}>Status</span>
                      <span className={styles.status}>
                        {taskDetails?.statusName || "N/A"}
                      </span>
                    </div>
                    <div className={styles.metaItem}>
                      <span className={styles.metaLabel}>Assigned To</span>
                      <span className={styles.assignedTo}>
                        <UserOutlined className={styles.userIcon} />
                        {taskDetails?.assignedUser &&
                        taskDetails.assignedUser.length > 0
                          ? taskDetails.assignedUser
                              .map((u: any) => u.fullName || u.full_name)
                              .join(", ")
                          : taskDetails?.employeeName || "Unassigned"}
                      </span>
                    </div>
                    <div className={styles.metaItem}>
                      <span className={styles.metaLabel}>Estimated Time</span>
                      <span className={styles.timeEstimate}>
                        {taskDetails?.estimatedTime
                          ? `${taskDetails?.estimatedTime}m`
                          : "N/A"}
                      </span>
                    </div>
                    <div className={styles.metaItem}>
                      <span className={styles.metaLabel}>Actual Time</span>
                      <span className={styles.timeEstimate}>
                        {taskDetails?.actualTime
                          ? `${taskDetails?.actualTime}m`
                          : "N/A"}
                      </span>
                    </div>
                  </div>
                  <div
                    className={styles.dateInfo}
                    style={{
                      marginTop: "12px",
                      padding: "12px",
                      background: "#f8fafc",
                      borderRadius: "var(--bs-border-radius)",
                    }}
                  >
                    <span className={styles.dateLabel}>Start:</span>
                    <span className={styles.dateValue}>
                      {taskDetails?.startDateTime
                        ? dayjs(taskDetails?.startDateTime).format(
                            "MMM D, YYYY",
                          )
                        : "Not set"}
                    </span>
                    <span className={styles.dateSeparator}>•</span>
                    <span className={styles.dateLabel}>Due:</span>
                    <span className={styles.dateValue}>
                      {taskDetails?.endDateTime
                        ? dayjs(taskDetails?.endDateTime).format("MMM D, YYYY")
                        : "Not set"}
                    </span>
                  </div>
                </div>

                <div className={styles.section}>
                  <h3 className={styles.sectionTitle}>Description</h3>
                  <div
                    className={styles.description}
                    dangerouslySetInnerHTML={{
                      __html:
                        taskDetails?.description || "No description provided",
                    }}
                  />
                </div>

                {!isNotificationView &&
                  Utils.isUserHasAccess(
                    context?.user?.privilegeList ?? [],
                    ePrivileges.VIEWTASKTIMETRACKING,
                  ) && (
                    <div className={styles.timeTrackingSection}>
                      <div className={styles.timeTrackingHeader}>
                        <h3 className={styles.sectionTitle}>Time Tracking</h3>
                        <Button
                          type="primary"
                          size="small"
                          // className={styles.saveButton}
                          onClick={() => {
                            if (actualTime) {
                              updateTask({
                                ...taskDetails,
                                actualTime: actualTime,
                                statusId: currentDropData
                                  ? currentDropData?.targetCategory?.statusId
                                  : taskDetails.statusId,
                              });
                            } else {
                              onShowToast({
                                type: ToastType.error,
                                title: <CloseOutlined />,
                                position: ToastOpen.centerTop,
                                content:
                                  "Please enter the actual time before updating the task.",
                              });
                            }
                          }}
                        >
                          Save
                        </Button>
                      </div>
                      <div className={styles.timeGrid}>
                        <div className={styles.timeCard}>
                          <div
                            style={{
                              display: "grid",
                              gridTemplateColumns: "1fr 1fr",
                              gap: 8,
                              marginBottom: 8,
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                              }}
                            >
                              <span className={styles.timeLabel}>
                                Move From
                              </span>
                              <ArrowRightOutlined style={{ marginRight: 8 }} />
                            </div>
                            <span className={styles.timeLabel}>Move To</span>
                            <span
                              className={styles.timeValue}
                              style={{ minWidth: 90 }}
                            >
                              {taskDetails?.statusName || "N/A"}
                            </span>
                            <Select
                              style={{ minWidth: 140 }}
                              value={
                                currentDropData?.targetCategory?.statusId ||
                                taskDetails?.statusId
                              }
                              options={
                                statusOptions?.map((opt) => ({
                                  label: opt.label,
                                  value: opt.value,
                                })) || []
                              }
                              onChange={(value) => {
                                setTargetStatusId && setTargetStatusId(value);
                              }}
                              disabled={isNotificationView}
                            />
                          </div>
                        </div>

                        <div className={styles.timeCard}>
                          <div className={styles.timeInputHeader}>
                            <span className={styles.timeLabel}>
                              {taskDetails?.actualTime
                                ? "Update Time"
                                : "Log Time"}
                            </span>
                            {taskDetails?.actualTime && (
                              <div className={styles.currentTimeDisplay}>
                                Current: {taskDetails.actualTime}m
                              </div>
                            )}
                          </div>
                          <Form form={form} className={styles.timeForm}>
                            <Form.Item name="actualTime" style={{ margin: 0 }}>
                              <div className={styles.timeInputGroup}>
                                <InputNumber
                                  min={1}
                                  max={1000}
                                  placeholder="Enter minutes"
                                  value={actualTime}
                                  onKeyDown={(e) => {
                                    if (
                                      !/^\d$/.test(e.key) &&
                                      e.key !== "Backspace" &&
                                      e.key !== "Enter" &&
                                      e.key !== "Tab"
                                    ) {
                                      e.preventDefault();
                                    }
                                  }}
                                  onChange={(value) => setActualTime(value)}
                                  className={styles.timeInput}
                                />
                              </div>
                            </Form.Item>
                          </Form>
                        </div>
                      </div>
                    </div>
                  )}

                {/* comment accordian */}
                <div className={styles.commentHistory}>
                  <Collapse
                    activeKey={activeCommentKeys}
                    onChange={(keys) =>
                      setActiveCommentKeys(Array.isArray(keys) ? keys : [keys])
                    }
                    bordered={false}
                  >
                    <Panel
                      header={`Comment History (${comments.length})`}
                      key="1"
                      className={styles.commentAccordion}
                    >
                      {comments.map((comment) => (
                        <div key={comment.id} className={styles.commentItem}>
                          <div className={styles.commentHeader}>
                            <span className={styles.commentUser}>
                              {comment.user}
                            </span>
                            <span className={styles.commentTime}>
                              {dayjs(comment.timestamp).format("MMM D, h:mm A")}
                            </span>
                          </div>
                          <div className={styles.commentContent}>
                            {comment.content}
                          </div>
                        </div>
                      ))}
                    </Panel>
                  </Collapse>
                </div>
                {/* Attachment Accordion */}
                <div className={styles.commentHistory}>
                  <Collapse
                    activeKey={activeFileKeys}
                    onChange={(keys) =>
                      setActiveFileKeys(Array.isArray(keys) ? keys : [keys])
                    }
                    bordered={false}
                  >
                    <Panel
                      header={`Attachments (${taskDetails?.taskdocuments?.length || 0})`}
                      key="1"
                      className={styles.commentAccordion}
                    >
                      <div className={styles.responsiveTable}>
                        <Table
                          className={styles.taskTable}
                          columns={[
                            {
                              title: "Preview",
                              dataIndex: "docName",
                              key: "preview",
                              width: 80,
                              render: (fileName, record: any) => {
                                const fullUrl = `${process.env.REACT_APP_PUBLIC_API_IMAGEURL}${record.docURL}`;
                                return isImageFile(fileName) ? (
                                  <Image
                                    width={50}
                                    height={50}
                                    src={fullUrl}
                                    style={{
                                      objectFit: "cover",
                                      borderRadius: "4px",
                                    }}
                                    preview={{
                                      mask: (
                                        <div style={{ fontSize: "12px" }}>
                                          Preview
                                        </div>
                                      ),
                                    }}
                                  />
                                ) : (
                                  <div
                                    style={{
                                      width: 50,
                                      height: 50,
                                      backgroundColor: "#f5f5f5",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      borderRadius: "4px",
                                      fontSize: "12px",
                                      color: "#999",
                                    }}
                                  >
                                    File
                                  </div>
                                );
                              },
                            },
                            {
                              title: "File Name",
                              dataIndex: "docName",
                              key: "doc_name",
                              render: (fileName) => (
                                <div style={{ wordBreak: "break-word" }}>
                                  {fileName}
                                </div>
                              ),
                            },
                            {
                              title: "Uploaded By",
                              dataIndex: "createdByUser",
                              key: "createdby",
                              render: (createdByUser) =>
                                createdByUser?.fullName || "-",
                            },
                            {
                              title: "Action",
                              dataIndex: "docURL",
                              key: "docURL",
                              align: "center",
                              render: (url, record) => (
                                <EyeOutlined
                                  style={{
                                    cursor: "pointer",
                                    fontSize: "16px",
                                  }}
                                  onClick={() =>
                                    window.open(
                                      `${process.env.REACT_APP_PUBLIC_API_IMAGEURL}${url}`,
                                      "_blank",
                                    )
                                  }
                                />
                              ),
                            },
                          ]}
                          dataSource={taskDetails?.taskdocuments || []}
                          pagination={{
                            pageSize: 8,
                            showSizeChanger: false,
                            hideOnSinglePage: true,
                          }}
                          rowKey='docURL'
                          scroll={{ x: true }}
                        />
                      </div>

                      {/* Add New Attachments Section */}
                      {!isNotificationView && (
                        <div
                          style={{
                            marginTop: "20px",
                            padding: "16px",
                            backgroundColor: "#fafafa",
                            borderRadius: "8px",
                            border: "1px dashed #d9d9d9",
                          }}
                        >
                          <h4
                            style={{
                              margin: "0 0 12px 0",
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                              color: "#595959",
                            }}
                          >
                            <PaperClipOutlined />
                            Add New Attachments
                          </h4>

                          <Upload
                            multiple
                            fileList={fileList}
                            onChange={handleFileChange}
                            beforeUpload={beforeUpload}
                            onRemove={(file) => {
                              const index = fileList.indexOf(file);
                              const newFileList = fileList.slice();
                              newFileList.splice(index, 1);
                              setFileList(newFileList);
                            }}
                            style={{ width: "100%" }}
                          >
                            <Button
                              icon={<UploadOutlined />}
                              style={{ marginBottom: "8px" }}
                              disabled={uploading}
                            >
                              Select Files
                            </Button>
                          </Upload>

                          {fileList.length > 0 && (
                            <div style={{ marginTop: "12px" }}>
                              <Button
                                type="primary"
                                onClick={uploadAttachments}
                                loading={uploading}
                                style={{ marginRight: "8px" }}
                              >
                                Upload {fileList.length} File
                                {fileList.length > 1 ? "s" : ""}
                              </Button>
                              <Button
                                onClick={() => setFileList([])}
                                disabled={uploading}
                              >
                                Clear All
                              </Button>
                            </div>
                          )}

                          <div
                            style={{
                              fontSize: "12px",
                              color: "#8c8c8c",
                              marginTop: "8px",
                            }}
                          >
                            Maximum file size: 10MB per file
                          </div>
                        </div>
                      )}
                    </Panel>
                  </Collapse>
                </div>

                <div className={styles.commentSection}>
                  <h3 className={styles.sectionTitle}>Add Comment</h3>
                  <div className={styles.editorContainer}>
                    <JoditEditor
                      ref={editor}
                      value={comment}
                      config={config}
                      onBlur={(newContent) => setComment(newContent)}
                      onChange={(newContent) => {}}
                    />
                  </div>
                  <div className={styles.commentActions}>
                    <div className={styles.commentForm}>
                      {!isNotificationView && (
                        <div className={styles.notifySection}>
                          <label className={styles.notifyLabel}>
                            Notify Users
                          </label>
                          <Select
                            mode="multiple"
                            placeholder="Select users to notify"
                            className={styles.notifySelect}
                            showSearch
                            value={memberIds}
                            filterOption={(input, option) =>
                              String(option?.label ?? "")
                                .toLowerCase()
                                .includes(input.toLowerCase())
                            }
                            onChange={(selectedUsers) =>
                              setMemberIds(selectedUsers)
                            }
                            options={memberOptions.map((user: any) => ({
                              label: user.name,
                              value: user.id,
                            }))}
                          />
                        </div>
                      )}
                      <Button
                        type="primary"
                        // className={styles.submitButton}
                        onClick={() => postComment(taskDetails?.id)}
                      >
                        Post Comment
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </TabPane>
            {Utils.isUserHasAccess(
              context.user?.privilegeList ?? [],
              ePrivileges.VIEWTASKHISTORY,
            ) && (
              <TabPane tab="History" key="2" className={styles.modalContainer}>
                <div className={styles.historyTab}>
                  <TimelineComponent history={historyDetails?.getTaskHistory} />
                </div>
              </TabPane>
            )}
          </Tabs>
        </div>
      ) : (
        <div className={styles.loadingContainer}>
          <Loader />
        </div>
      )}
    </>
  );
};

export default TaskDetailsModal;
